import { createServerFn } from '@tanstack/react-start'
import { getAttribution, mintClickId } from './attribution'

// Whatzap (first-party CRM) ingests two kinds of leads through this endpoint:
// the ebook-preview opt-in (with a phone number) and phone-less click beacons
// sent on every WhatsApp CTA click. Both are best-effort side channels;
// `generate_lead` in GA4 never depends on this endpoint or its API key.
const LEADS_API_BASE = (import.meta.env.VITE_BLOG_API_BASE_URL || 'https://whatzap.dropify.id').replace(/\/+$/, '')
const LEADS_KEY = import.meta.env.VITE_LEADS_API_KEY as string | undefined

export type WhatsAppLeadInput = {
  phone?: string
  phone_sha256?: string
  click_id?: string
  lead_source?: string
  page_path?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  fbclid?: string
  ttclid?: string
  msclkid?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

// Runs only on the SSR server (invoked via the server function below).
async function postLead(input: WhatsAppLeadInput): Promise<void> {
  if (!LEADS_KEY) return
  try {
    await fetch(`${LEADS_API_BASE}/api/v1/leads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Lead-Key': LEADS_KEY,
      },
      body: JSON.stringify(input),
    })
  } catch {
    // Lead capture is best-effort; a failure must never surface to the visitor.
  }
}

export const submitWhatsAppLead = createServerFn({ method: 'POST' })
  .inputValidator((data: WhatsAppLeadInput) => data)
  .handler(({ data }) => postLead(data))

const ATTRIBUTION_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ttclid',
  'msclkid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

/**
 * Assemble the phone-less click payload: a minted click id, the CTA source and
 * page, plus every stored ad-click identifier (gclid/gbraid/wbraid for Google
 * Ads, fbclid for Meta, ttclid/msclkid, and UTMs). Returns null outside the
 * browser.
 */
export function buildWhatsAppClickLeadPayload(leadSource: string): WhatsAppLeadInput | null {
  if (typeof window === 'undefined') return null
  const attribution = getAttribution()
  const payload: WhatsAppLeadInput = {
    click_id: mintClickId(),
    lead_source: leadSource,
    page_path: window.location.pathname,
  }
  for (const key of ATTRIBUTION_KEYS) {
    if (attribution[key]) payload[key] = attribution[key]
  }
  return payload
}

/**
 * Phone-less click beacon for Whatzap, fired alongside every `generate_lead`.
 * The click id plus attribution keeps the row reconcilable with the chat that
 * follows (by campaign, page, and time) without asking the visitor anything.
 * Fire-and-forget: must never block or break the WhatsApp redirect.
 */
export function captureWhatsAppClickLead(leadSource: string): void {
  try {
    const payload = buildWhatsAppClickLeadPayload(leadSource)
    if (!payload) return
    void submitWhatsAppLead({ data: payload }).catch(() => {})
  } catch {
    // Lead capture is best-effort; a failure must never surface to the visitor.
  }
}
